import { z } from 'zod'

import { TaskType } from '../ag-concepts'
import { EXPORT_SURFACES, exportSurfaceSchema } from '../data-export'
import { IMPORT_SURFACES, importSurfaceSchema } from '../data-import'
import { regionCollectionSchema } from '../geojson-schemas'
import { baseCffSchema } from '../locutus/plan'
import { taskSchema } from '../pangea'
import { DataSource } from '../shared'
import { controllableApplicationTypes } from '../stitch/applications'
import { lassoFilterEntrySchema } from '../stitch/lasso'
import { includedDataToggleSchema } from '../tailor/reporting-toggles'
import { ReportType } from '../tailor/seasonal-task-report'
import { AURORA_CONTROL_DESCRIPTIONS, AuroraControlId } from './controls'
import { changeRateMetricSchema } from './voice-command'

/**
 * A data-layer filter as reported by DescribeCurrentView — the wire filter entry
 * (a stream/application/layer plus a discrete or continuous range) plus the
 * include/exclude direction the editor tracks. Only currently-applied filters
 * are reported, so there is no disabled flag here.
 */
const appliedFilterSchema = lassoFilterEntrySchema.extend({
  type: z.enum(['include', 'exclude'])
})

/**
 * The map's active lasso selection as reported by DescribeCurrentView: the
 * include/exclude direction, the selected area in acres, whether it is currently
 * constraining the data (a selection is preserved across mode switches, so it can
 * exist without being active), and the polygon geometry as GeoJSON.
 */
const lassoSelectionSchema = z.object({
  mode: z.enum(['include', 'exclude']),
  active: z
    .boolean()
    .describe('Whether the selection is currently filtering the data, vs. merely preserved.'),
  selectedAreaAcres: z.number().describe('Area enclosed by the selection, in acres.'),
  geojson: regionCollectionSchema.describe('The lasso polygon(s) as a GeoJSON FeatureCollection.')
})

/** Who may invoke a client-dispatched tool, and from where. */
export enum ClientDispatchedToolExposure {
  /**
   * Composed into the declaring connection's own agent catalog. The
   * conversation running on this connection can invoke it.
   */
  Agent = 'Agent',
  /**
   * A cloud-side wrapper picks the target connection, so chats elsewhere in
   * the org may invoke it on this client — declaring support consents to
   * org-wide invocation.
   */
  CloudRouted = 'CloudRouted'
}

/**
 * A tool's exposures, as a set rather than a single value: the two are
 * independent grants, and a tool can legitimately hold both. `Alert` does —
 * this connection's own assistant shows its user a message, *and* a relayed
 * operator-to-operator message arrives the same way. Modelling that as one
 * value forces a choice between dropping the tool from the declaring
 * connection's own catalog and duplicating it under a second id.
 */
type ClientDispatchedToolDefinition<
  Exposure extends readonly ClientDispatchedToolExposure[],
  Args extends z.ZodType<Record<string, unknown>>,
  Result extends z.ZodType,
  RequiresPermission extends boolean = false
> = {
  exposure: Exposure
  description: string
  argsSchema: Args
  resultSchema: Result
  timeoutMs?: number
  requiresPermission?: RequiresPermission
  spokenOnCompletion?: true
}

/**
 * Statically-known registry of tools the cloud can ask the client to
 * execute via the Dispatch envelope (Phase 0 protocol).
 *
 * The wire stays loose — `supportedTools` is `string[]` and dispatched
 * args are `Record<string, unknown>` — so new tools can be added cloud-side
 * without coordinated client releases. This registry is the *application*
 * layer of types: shared between cloud and TS clients so call sites get
 * real schemas for args and results.
 *
 * Each tool definition carries:
 *   - exposure: who may invoke the tool, as a set. Agent-exposed entries are
 *     composed into the declaring connection's own catalog; cloud-routed
 *     entries are invoked by a cloud-side wrapper that picks the target
 *     connection, and are the only entries dispatchable to a connection other
 *     than the chat's own. An entry may hold both.
 *   - description: model-facing tool description. Iterating on this is
 *     the registry's primary value — change the prose, restart the cloud,
 *     no client release needed. (For cloud-routed entries no model reads
 *     it; it documents the contract for the wrapper and client authors.)
 *   - argsSchema: zod schema validated at the dispatch handler boundary
 *     on the client. Models that produce a top-level JSON object.
 *   - resultSchema: zod schema for the value the client returns. The
 *     cloud-side dispatcher validates it before resolving the agent's
 *     pending tool call.
 *   - timeoutMs: how long the cloud waits for the client's reply, for a tool
 *     that needs longer than the dispatcher's default.
 *   - spokenOnCompletion: the tool's result answers the request by itself,
 *     so the user can hear it before the agent's reply arrives.
 *   - requiresPermission: the tool changes the user's data, so the user
 *     approves each call before the cloud dispatches it. The approval card is
 *     built from the call's args alone, so a tool that should tell the user
 *     what it changes, such as which field's boundary it saves, takes that in
 *     its args, and its handler refuses a call whose args disagree with what
 *     it would change.
 */
const defineClientDispatchedTool = <
  Exposure extends readonly ClientDispatchedToolExposure[],
  Args extends z.ZodType<Record<string, unknown>>,
  Result extends z.ZodType,
  RequiresPermission extends boolean = false
>(
  definition: ClientDispatchedToolDefinition<Exposure, Args, Result, RequiresPermission>
): ClientDispatchedToolDefinition<Exposure, Args, Result, RequiresPermission> => definition

/** Severity of an in-cab (20|20) alarm event. */
const alarmSeveritySchema = z.enum(['alert', 'alarm'])

/** Screens the in-cab (20|20) display can navigate to. */
const displayScreenIdSchema = z.enum([
  'dashboard',
  'setup',
  'field_select',
  'seeding_config',
  'liquid_config',
  'granular_config',
  'equipment_setup',
  'event_log',
  'downforce_control',
  'row_details'
])

const alarmMetadataSchema = z.object({
  metrics: z
    .array(z.string())
    .describe(
      'Metrics in an abnormal state at onset. By application type — seed: population, singulation, vacuum, sri, spacing; liquid: flow; granular: rate, flow, estimatedParticles, particlesDeviation; force: groundContact, margin, averageForce; pressure: pressure.'
    ),
  affectedRows: z
    .array(z.number().int())
    .optional()
    .describe(
      '1-based row numbers abnormal at onset. Omitted for implement-wide alarms with no per-row breakdown.'
    )
})

const alarmEventBaseSchema = z.object({
  application: z
    .string()
    .describe(
      'Human-readable name of the application that alarmed (e.g. "Population", "Vacuum", "Down Force").'
    ),
  severity: alarmSeveritySchema.describe(
    "'alert' — audible alert threshold crossed; 'alarm' — audible failure/alarm threshold crossed."
  ),
  startedMinutesAgo: z
    .number()
    .int()
    .describe('Minutes since the alarm started, computed at query time.'),
  startLatitude: z
    .number()
    .optional()
    .describe(
      'GPS latitude (decimal degrees) when the alarm first triggered. Omitted if no GPS fix was available.'
    ),
  startLongitude: z
    .number()
    .optional()
    .describe(
      'GPS longitude (decimal degrees) when the alarm first triggered. Omitted if no GPS fix was available.'
    ),
  metadata: alarmMetadataSchema
    .optional()
    .describe('Structured detail about what was wrong when the alarm triggered.')
})

/**
 * One alarm/alert transition. Discriminated on `active` so the union is
 * explicit rather than hiding `durationMinutes` behind an optional: a
 * resolved event (active: false) always carries how long it lasted, and an
 * active one never does.
 */
const alarmEventSchema = z.discriminatedUnion('active', [
  alarmEventBaseSchema.extend({ active: z.literal(true) }),
  alarmEventBaseSchema.extend({
    active: z.literal(false),
    durationMinutes: z
      .number()
      .int()
      .min(1)
      .describe(
        'How long the alarm lasted, in minutes (minimum 1). Present only when the alarm has resolved.'
      )
  })
])

export enum BoundaryCreatorOutcome {
  Generated = 'Generated',
  NotRegistered = 'NotRegistered',
  NoPasses = 'NoPasses',
  Failed = 'Failed'
}

export enum BoundarySaveOutcome {
  Saved = 'Saved',
  HasExistingBoundary = 'HasExistingBoundary',
  NothingToSave = 'NothingToSave',
  Failed = 'Failed'
}

const boundarySourceKeySchema = z.object({
  season: z.number().int(),
  task: z.enum(TaskType),
  dataSource: z.enum(DataSource)
})

const boundarySourceSchema = z.object({
  season: z.number().int(),
  task: z.enum(TaskType),
  dataSources: z.array(z.enum(DataSource)),
  passCount: z.number().int().describe('How many passes in the group were outlined together.')
})

export const OPEN_BOUNDARY_CREATOR_TIMEOUT_MS = 60_000

export enum PrescriptionZoningOutcome {
  Opened = 'Opened',
  NotFound = 'NotFound',
  Ambiguous = 'Ambiguous',
  NoBoundary = 'NoBoundary',
  Failed = 'Failed'
}

export enum ZoningStatus {
  Ready = 'Ready',
  Failed = 'Failed'
}

export enum ZoningMethod {
  NaturalBreaks = 'naturalBreaks',
  EqualCount = 'equalCount',
  EqualInterval = 'equalInterval'
}

export enum ZoneEdges {
  Straight = 'straight',
  Rounded = 'rounded',
  VeryRounded = 'veryRounded'
}

export const ZONING_TIMEOUT_MS = 60_000

const zoningSchema = z.discriminatedUnion('status', [
  z.object({
    status: z.literal(ZoningStatus.Ready),
    source: z.string().describe('The layer the zones come from, as the screen names it.'),
    zoneCount: z.number().int(),
    recommendedZoneCount: z
      .number()
      .int()
      .nullable()
      .describe('The count that best balances fit against fragmentation, or null if none does.'),
    customBreaks: z
      .boolean()
      .describe('Whether the user dragged the breaks rather than taking a count.'),
    candidates: z
      .array(
        z.object({
          zoneCount: z.number().int(),
          fit: z.number().describe("Share of the layer's variation the zones explain, 0 to 1."),
          smoothed: z
            .number()
            .nullable()
            .describe(
              'Share of the field moved into a neighbouring zone by merging small pieces and rounding edges, 0 to 1; null for soil layers, which are not smoothed.'
            )
        })
      )
      .describe(
        'Empty for a soil survey class layer such as soil type, which makes one zone per class, so its zone count, break method, and edges cannot be changed.'
      ),
    method: z.enum(ZoningMethod),
    smallestZoneAcres: z.number().nullable().describe('Null for soil layers.'),
    edges: z.enum(ZoneEdges).nullable().describe('Null for soil layers.'),
    unit: z.string().nullable().describe("The layer's unit, which zone ranges are given in."),
    zones: z.array(
      z.object({
        name: z.string().describe("The zone's value range, or its class for a soil survey class."),
        acres: z.number()
      })
    )
  }),
  z.object({
    status: z.literal(ZoningStatus.Failed),
    reason: z.string().describe('Why no zones could be made — tell the user this.')
  })
])

export const prescriptionProductSchema = z.object({
  applicationType: controllableApplicationTypes
    .nullable()
    .describe('What the prescription applies, such as seeding for a population prescription.'),
  name: z
    .string()
    .nullable()
    .describe(
      "The product's prescription name, in the user's language. Each product saves as its own prescription."
    ),
  rateColumns: z
    .array(z.string())
    .min(1)
    .nullable()
    .describe(
      'The names of its rate columns, in order. Most prescriptions have one; some apply several rates at once, such as separate nitrogen and phosphorus rates from one pass.'
    )
})

export type PrescriptionProduct = z.infer<typeof prescriptionProductSchema>

const ZONING_RESULT_GUIDE =
  'fit is the share of the layer’s variation the zones explain; smoothed is the share of the field that merging small pieces and rounding edges moved into a neighbouring zone, so a high smoothed share means the zones no longer follow the layer closely.'

/** The import surfaces as OpenImportScreen offers them: the argument value, then what it opens. */
const importSurfaceOptions = Object.entries(IMPORT_SURFACES)
  .map(([surface, guide]) => `'${surface}' (${guide.subject})`)
  .join('; ')

/** The export surfaces as OpenExportScreen offers them: the argument value, then what it opens. */
const exportSurfaceOptions = Object.entries(EXPORT_SURFACES)
  .map(([surface, guide]) => `'${surface}' (${guide.subject})`)
  .join('; ')

export const clientDispatchedTools = {
  /**
   * Dual-exposure, and the only entry with more senders than the model that
   * reads its description: `send_message_to_machine` relays an operator's
   * message through it, and the scheduled Sprout summary dispatches it with no
   * conversation behind it at all. The description names those paths rather
   * than describing the out-of-scope reply alone, so the constraint it places
   * on wording — stand alone, assume no thread — reads as the consequence it
   * is instead of an unexplained style rule.
   */
  Alert: defineClientDispatchedTool({
    exposure: [ClientDispatchedToolExposure.Agent, ClientDispatchedToolExposure.CloudRouted],
    description:
      'Show the user a brief push notification. The client presents it on its own, outside the flow of the conversation, and owns the heading, dismissal, and whether the user is interruptible right now — so the text has to stand by itself. The cloud sends these unprompted as well, relaying a message from another operator or a finished pass summary, which is why nothing sent this way can read as a continuation of a conversation. The one reason for you to send one yourself is that the user asked about something clearly outside the scope of agricultural operations (e.g. general trivia, unrelated topics, programming help): send a short, polite explanation of why the request is out of scope. Do not use it for borderline cases like weather; answer those normally.',
    argsSchema: z.object({
      message: z.string().describe('The text to display to the user.'),
      /**
       * Echoed to the recipient as the sender's name, and present only when the
       * cloud is notifying on someone's behalf. Absent for the out-of-scope
       * reply and the scheduled summary, which are Aurora speaking for itself —
       * so a client must treat it as optional and render unattributed without it.
       */
      source: z
        .string()
        .optional()
        .describe(
          "Display name of whoever this message is from, when it is relayed on a person or machine's behalf. Omit when the message is Aurora's own."
        )
    }),
    resultSchema: z.object({
      shown: z.boolean()
    })
  }),
  EchoBack: defineClientDispatchedTool({
    exposure: [ClientDispatchedToolExposure.Agent],
    description:
      'Testing-only echo: ask the client to repeat back a string and report when it received it. Use only when the user explicitly asks Aurora to "echo" something. Mention the returned timestamp in your reply so the user can see the dispatch round-tripped.',
    argsSchema: z.object({
      text: z.string().describe('The text to echo back.')
    }),
    resultSchema: z.object({
      echo: z.string(),
      receivedAt: z
        .string()
        .describe('ISO-8601 timestamp the client stamped when it received the dispatch.')
    })
  }),
  // The agent-facing surface is get_live_machine_performance in services/aurora,
  // which picks the target connection; this entry is the client contract and
  // the capability flag a connection declares via SetSupportedTools.
  GetLiveMachinePerformance: defineClientDispatchedTool({
    exposure: [ClientDispatchedToolExposure.CloudRouted],
    description:
      'Fetch the live performance snapshot from an in-field machine (20|20 display): per-system application rates, total acres covered, acres per hour, ground speed, and related operating stats. The exact keys vary by machine and active operation.',
    argsSchema: z.object({}),
    // Loose by design: the display (C++) owns the payload and will iterate
    // on keys without coordinated cloud releases. Tighten to known keys
    // once the ui2020 payload contract settles.
    resultSchema: z.record(z.string(), z.unknown())
  }),
  DescribeCurrentView: defineClientDispatchedTool({
    exposure: [ClientDispatchedToolExposure.Agent],
    description:
      'Ask the client to describe what the user is currently looking at: the field they\'re viewing (if any), the URL path they\'re on, and — when they are on the map — the data-layer filters and lasso selection currently applied. Use this when the user\'s question depends on what they see right now (e.g., "what\'s on my screen?", "what page am I on?", "what am I filtering to?", "what did I lasso?", or follow-ups about the current field that need more than the client_context block already provides). Prefer client_context for the client, farm, field and the viewed layer; use this tool when you need fresher state or fields client_context doesn\'t cover. The URL path encodes the active layer, split-comparison, record, and mode — parse it for those. `filters` and `lasso` are empty/null unless the user is on the map.',
    argsSchema: z.object({}),
    resultSchema: z.object({
      viewing: z
        .record(z.string(), z.unknown())
        .nullable()
        .describe('The currentlyViewing slot if populated, or null if the user is not on a field.'),
      url: z.string().describe('The browser URL path (pathname + search) the user is on.'),
      filters: z
        .array(appliedFilterSchema)
        .describe(
          'Data-layer filters currently applied on the map (each a stream/application/layer with an include/exclude range). Empty when none are applied or the user is not on the map.'
        ),
      lasso: lassoSelectionSchema
        .nullable()
        .describe(
          'The map lasso selection, or null when there is none or the user is not on the map.'
        )
    })
  }),
  /**
   * In-cab (20|20) control. The metric *is* the application identity —
   * each application targets exactly one metric — so the model never has
   * to reason about applications as a separate concept. Only the display
   * declares support for this; web/mobile never see it.
   */
  ChangeRate: defineClientDispatchedTool({
    spokenOnCompletion: true,
    exposure: [ClientDispatchedToolExposure.Agent],
    description:
      "Change a live application rate on the in-cab 20|20 display. `metric` names the application to adjust — each application targets exactly one: 'population' (seeds per acre), 'nitrogen' (lbs/acre of nitrogen), or 'downforce' (lbs). `value` is the absolute target rate to set. Use when the operator asks to set, increase, or decrease one of these (e.g. 'set seed rate to 35000', 'bump nitrogen to 150', 'drop downforce to 80'). Interpret shorthand like '35k' as 35000. For a relative change ('increase by 2000'), compute the target from the current rate.",
    argsSchema: z.object({
      metric: changeRateMetricSchema.describe(
        'Which application rate to change. Each application targets exactly one metric.'
      ),
      value: z.number().positive().describe('The absolute target rate to set for that metric.')
    }),
    resultSchema: z.object({
      success: z.boolean()
    })
  }),
  /**
   * In-cab (20|20) field activation. CFF-based, and not tied to any historical
   * data, unlike SetAnalysisField, which only switches what stream is being analyzed
   */
  SetActiveField: defineClientDispatchedTool({
    spokenOnCompletion: true,
    exposure: [ClientDispatchedToolExposure.Agent],
    description:
      "Switch the active field on the in-cab 20|20 display — the field the operator is currently working — identified by client, farm, and field name. The display activates it. Resolve the operator's spoken reference to an exact client/farm/field using GetActivatableFieldList before calling.",
    argsSchema: z.object({
      client: z.string().describe('Client name of the field to activate.'),
      farm: z.string().describe('Farm name of the field to activate.'),
      field: z.string().describe('Field name to activate.')
    }),
    resultSchema: z.object({
      success: z.boolean()
    })
  }),
  /**
   * Companion lookup for SetActiveField: the set of fields the display can
   * activate, as client/farm/field tuples. Named to signal it queries the
   * *client* for what it can activate – distinct from any future cloud-side
   * field list that needs no client round-trip.
   */
  GetActivatableFieldList: defineClientDispatchedTool({
    exposure: [ClientDispatchedToolExposure.Agent],
    description:
      "List the fields the in-cab 20|20 display can activate for this operation, as client/farm/field tuples. Call this to resolve a field the operator names to an exact client/farm/field, then pass that to SetActiveField. Returns every available field; match the operator's reference charitably.",
    argsSchema: z.object({}),
    resultSchema: z.object({
      fields: z.array(
        z.object({
          client: z.string(),
          farm: z.string(),
          field: z.string()
        })
      )
    })
  }),
  /**
   * In-cab (20|20) alarm log. Reports alarm/alert events over time, not
   * instantaneous per-row state. Only the display declares support.
   */
  GetAlarmHistory: defineClientDispatchedTool({
    exposure: [ClientDispatchedToolExposure.Agent],
    description:
      'Get the in-cab 20|20 display\'s alarm/alert history for this session: a newest-first log of alarm transitions across seeding (population), liquid, granular, down force, and pressure applications. Use when the operator asks whether anything has alarmed, what alarmed, whether something is still alarming, or how long an alarm lasted (e.g. "have there been any alarms?", "is the vacuum still alarming?", "what was the last thing that alarmed?"). Each entry names the application, its severity, when it started, whether it is still active, and — for resolved alarms — how long it lasted. An application can appear more than once if it alarmed, cleared, and alarmed again. An empty list means nothing has alarmed this session.',
    argsSchema: z.object({}),
    resultSchema: z.object({
      alarms: z
        .array(alarmEventSchema)
        .describe(
          'Alarm transitions newest-first (index 0 is most recent). Empty if nothing alarmed this session.'
        )
    })
  }),
  /**
   * In-cab (20|20) screen navigation. Named NavigateDisplayTo — not
   * NavigateTo — so it never collides with a future web/mobile navigation
   * tool. Only the display declares support; web/mobile never see it.
   * Unknown-screen and no-applications failures come back as the dispatch
   * protocol's client_error outcome, not in resultSchema.
   */
  NavigateDisplayTo: defineClientDispatchedTool({
    spokenOnCompletion: true,
    exposure: [ClientDispatchedToolExposure.Agent],
    description:
      'Navigate the in-cab 20|20 display to a screen on behalf of the operator; the display switches immediately. Use when the operator asks to go somewhere or open something (e.g. "show me my product config", "take me to field selection", "open setup", "show the event log", "go back to the dashboard"). For `row_details`, also pass the 1-based `rowNum`. `row_details` uses the first configured seeding application and `downforce_control` the first configured downforce application; either fails if the implement has none configured. For a screen not in the list, navigate to the closest parent and let the operator drill in from there.',
    argsSchema: z.object({
      screenId: displayScreenIdSchema.describe(
        'Which screen to navigate to: dashboard, setup, field_select, seeding_config, liquid_config, granular_config, equipment_setup, event_log, downforce_control, or row_details.'
      ),
      rowNum: z
        .number()
        .int()
        .positive()
        .optional()
        .describe(
          '1-based row number. Required when screenId is "row_details"; ignored for every other screen.'
        )
    }),
    resultSchema: z.object({
      screenId: displayScreenIdSchema.describe('The screen the display navigated to.'),
      rowNum: z
        .number()
        .int()
        .positive()
        .optional()
        .describe('Echoed row number, present only for row_details.')
    })
  }),
  /**
   * In-cab (20|20) power controls. Both resolve with null: the display
   * acknowledges before it acts, and for a restart there is nothing left to
   * report it over once it does.
   */
  RestartDisplay: defineClientDispatchedTool({
    spokenOnCompletion: true,
    exposure: [ClientDispatchedToolExposure.Agent],
    description:
      'Reboot the in-cab 20|20 display. The screen goes dark, the display is unavailable until it finishes booting, and this conversation ends with it. Use only when the operator explicitly asks for a restart or reboot (e.g. "restart the display", "reboot the 20|20"); never offer it as a fix for an alarm, a stale reading, or a screen the operator cannot find. Keep any reply short, since the operator may not see it.',
    argsSchema: z.object({}),
    resultSchema: z.null()
  }),
  EnterStandby: defineClientDispatchedTool({
    spokenOnCompletion: true,
    exposure: [ClientDispatchedToolExposure.Agent],
    description:
      'Put the in-cab 20|20 display to sleep: the screen goes black while the display keeps running, and touching the screen wakes it. Use when the operator asks to sleep, black out, or turn off the screen (e.g. "put the display to sleep", "turn the screen off", "go to standby"). It does not power the display off or interrupt the current operation.',
    argsSchema: z.object({}),
    resultSchema: z.null()
  }),
  /**
   * Web/mobile navigation. Switches which stream is being analyzed in the
   * app, identified by streamId based on existing as-applied data in Panorama.
   */
  SetAnalysisField: defineClientDispatchedTool({
    spokenOnCompletion: true,
    exposure: [ClientDispatchedToolExposure.Agent],
    description:
      'Switch and configure the field currently being analyzed in the Panorama web/mobile app. At minimum supply the streamId to open a field. Optionally narrow the view to a specific application and layer (resolve their ids from GetStreamApplications first), and optionally supply a `comparison` layer to put the map into split-map mode — two layers shown side by side for visual comparison. Use when the user asks to look at, open, or pull up a field, drill into a particular layer, or compare two layers. Resolve the streamId from the field/pass catalog and the applicationId/layerId from GetStreamApplications before calling.',
    argsSchema: z.object({
      streamId: z.string().describe('The streamId of the field/activity to switch the view to.'),
      applicationId: z
        .string()
        .nullable()
        .describe(
          'The application to display (from GetStreamApplications), or null to keep the stream’s default application.'
        ),
      layerId: z
        .string()
        .nullable()
        .describe(
          'The layer within the application to display (from GetStreamApplications), or null to keep the application’s default layer.'
        ),
      comparison: z
        .object({
          streamId: z
            .string()
            .nullable()
            .describe(
              'Stream for the comparison pane, or null to compare layers within the primary `streamId`.'
            ),
          applicationId: z.string().describe('The comparison pane’s application id.'),
          layerId: z.string().describe('The comparison pane’s layer id.')
        })
        .nullable()
        .describe(
          'A second layer to show alongside the primary one in split-map mode, or null for a single-layer view. Set this only when the user wants to compare two layers.'
        )
    }),
    resultSchema: z.object({
      switched: z.boolean()
    })
  }),
  /**
   * Web/mobile map data filter. Narrows the field shown on the map to a subset
   * of one layer's values; the map's histogram surfaces the active filter.
   * Only the map page declares support, so it's in the agent catalog solely
   * while the user is viewing the map.
   */
  SetDataFilter: defineClientDispatchedTool({
    spokenOnCompletion: true,
    exposure: [ClientDispatchedToolExposure.Agent],
    description:
      "Filter the field shown on the Panorama map to a subset of one layer's values, so the map (and its histogram) show only the matching area. For a DISCRETE layer (e.g. hybrid, variety, pass), pass `discreteCodes` — the numeric `code`s from get_layer_histogram bins for the values to keep/hide. For a CONTINUOUS layer (e.g. yield, downforce), pass `range` with the value bounds. `mode` 'include' keeps only the matching values, 'exclude' hides them. Resolve applicationId/layerId from GetStreamApplications, and discrete `code`s from get_layer_histogram, before calling. Calling again for the same layer replaces that layer's filter.",
    argsSchema: z.object({
      streamId: z.string().describe('The streamId of the field whose layer to filter.'),
      applicationId: z
        .string()
        .describe('The application of the layer to filter (from GetStreamApplications).'),
      layerId: z.string().describe('The layer to filter (from GetStreamApplications).'),
      mode: z
        .enum(['include', 'exclude'])
        .describe("'include' keeps only the matching values; 'exclude' hides them."),
      discreteCodes: z
        .array(z.number())
        .nullable()
        .describe(
          'For a discrete layer: the value `code`s (from get_layer_histogram bins) to filter to. Null for a continuous layer.'
        ),
      range: z
        .object({ minimum: z.number(), maximum: z.number() })
        .nullable()
        .describe(
          'For a continuous layer: the value range to filter to. Null for a discrete layer (use discreteCodes instead).'
        )
    }),
    resultSchema: z.object({
      applied: z.boolean()
    })
  }),
  OpenReport: defineClientDispatchedTool({
    spokenOnCompletion: true,
    exposure: [ClientDispatchedToolExposure.Agent],
    description:
      "Build a report in Panorama web: this opens the reporting page with the report configured and generated. Use it when the user asks for a report, a printout, or a summary document of a season's work — \"put together an agronomic report for last year's corn\", \"show me seasonal inputs for the Johnson farm\". The user lands on the report itself, where they can adjust it, print it, or export CSV, so you do not need to describe its contents back to them. Three report types: 'seasonal-inputs' lists the products and seed applied; 'condensed-season' is a one-page-per-field season overview; 'agronomic-activity' is the detailed per-pass report. When the user names the season and crop, pass them straight through: a year or crop the operation has no data for comes back with `opened` false and a reason to relay, not a report. Call GetAvailableSeasons only to fill in what the user left out, such as the most recent season when they named none, and not when this conversation already has its result. Resolve client, farm, and field names against the field catalog rather than passing what the user said verbatim. `includedData` adds sections on top of the ones the report already shows, and which ones a report honors depends on its type: 'seasonal-inputs' takes includeClients, includeFarms, includePasses; 'condensed-season' takes seeding, granular; 'agronomic-activity' takes seeding, granular, force, global, granularProducts, harvest, harvestLoadList, hybrids, implement, linearPosition, liquid, liquidProducts, pressure, thumbnail, weather. Pass null for anything the user did not ask for, which leaves the report on its own defaults.",
    argsSchema: z.object({
      reportType: z
        .enum(ReportType)
        .describe(
          "Which report to build: 'seasonal-inputs', 'condensed-season', or 'agronomic-activity'."
        ),
      season: z.number().int().describe('The season year the report covers, e.g. 2025.'),
      crop: z
        .string()
        .nullable()
        .describe(
          'The crop to report on, spelled as GetAvailableSeasons returns it (e.g. "Corn"), or null to cover every crop that season grew.'
        ),
      client: z.string().nullable().describe('Limit the report to one client, or null for all.'),
      farm: z.string().nullable().describe('Limit the report to one farm, or null for all.'),
      field: z.string().nullable().describe('Limit the report to one field, or null for all.'),
      dateRange: z
        .object({
          start: z.string().describe('First day to include, as YYYY-MM-DD.'),
          end: z.string().describe('Last day to include, as YYYY-MM-DD.')
        })
        .nullable()
        .describe(
          'Limit the report to passes run between these dates, or null to cover the whole season.'
        ),
      tasks: z
        .array(taskSchema)
        .nullable()
        .describe(
          'The task types to include (plant, spray, sidedress, striptill, harvest), or null for all of them.'
        ),
      deviceSerialNumbers: z
        .array(z.number().int())
        .nullable()
        .describe(
          'Limit the report to work done by these 20|20 serial numbers, or null for every device.'
        ),
      includedData: z
        .array(includedDataToggleSchema)
        .nullable()
        .describe(
          "Extra sections to add on top of the ones this report type already shows, from the set it honors. Null for just the report's own sections."
        )
    }),
    resultSchema: z.discriminatedUnion('opened', [
      z.object({
        opened: z.literal(true),
        url: z.string().describe('The reporting page the client navigated to.'),
        unsupportedIncludedData: z
          .array(z.string())
          .describe(
            'Requested sections this report type does not have. The report opened without them, so say so rather than reporting them as included.'
          )
      }),
      z.object({
        opened: z.literal(false),
        reason: z
          .string()
          .describe(
            'Why no report was opened — tell the user this rather than saying their report is ready.'
          )
      })
    ])
  }),
  /**
   * Web/mobile navigation to a file-import screen. The surfaces and the formats
   * they accept are the shared import catalog's to describe; this entry only
   * carries which one to open, so the two cannot disagree about what exists.
   */
  OpenImportScreen: defineClientDispatchedTool({
    spokenOnCompletion: true,
    exposure: [ClientDispatchedToolExposure.Agent],
    description: `Open the Panorama screen where the user imports their own files, for one kind of data: ${importSurfaceOptions}. Use it when the user asks to be taken there, asks where an import lives, or says they want to do the import now — "where do I upload my prescriptions", "take me to the boundary import", "I need to add my field list". Call describe_data_import instead when they only want to know which formats to use. Some screens are restricted for some organizations and members; a refusal comes back with \`opened\` false and a reason to relay, not a screen.`,
    argsSchema: z.object({
      surface: importSurfaceSchema.describe('Which kind of data the user wants to import.')
    }),
    resultSchema: z.discriminatedUnion('opened', [
      z.object({
        opened: z.literal(true),
        url: z.string().describe('The import screen the client navigated to.')
      }),
      z.object({
        opened: z.literal(false),
        reason: z
          .string()
          .describe(
            'Why the screen did not open — tell the user this rather than saying they are on it.'
          )
      })
    ])
  }),
  /**
   * Web/mobile navigation to a file-export screen. The surfaces and formats
   * are the shared export catalog's to describe; this entry only carries
   * which one to open. Unlike import, none of these screens is dedicated to
   * exporting — a boundary or prescription downloads from a row menu on its
   * planning screen, and as-applied data downloads in bulk from the Edit
   * screen — so `opened: true` lands the user on the screen, not on a
   * finished download.
   */
  OpenExportScreen: defineClientDispatchedTool({
    spokenOnCompletion: true,
    exposure: [ClientDispatchedToolExposure.Agent],
    description: `Open the Panorama screen where the user can export their data, for one kind of data: ${exportSurfaceOptions}. Use it when the user asks to be taken there, asks where an export lives, or says they want to export now — "where do I download my boundaries", "take me to export my prescriptions", "I need my field list as a file". Call describe_data_export instead of this when the user only wants to know which formats are available. Some screens are restricted for some organizations and members; a refusal comes back with \`opened\` false and a reason to relay, not a screen.`,
    argsSchema: z.object({
      surface: exportSurfaceSchema.describe('Which kind of data the user wants to export.')
    }),
    resultSchema: z.discriminatedUnion('opened', [
      z.object({
        opened: z.literal(true),
        url: z.string().describe('The export screen the client navigated to.')
      }),
      z.object({
        opened: z.literal(false),
        reason: z
          .string()
          .describe(
            'Why the screen did not open — tell the user this rather than saying they are on it.'
          )
      })
    ])
  }),
  OpenFieldRegistration: defineClientDispatchedTool({
    spokenOnCompletion: true,
    exposure: [ClientDispatchedToolExposure.Agent],
    description:
      'Open the Planning → Fields screen with every field that has passes but is not yet in planning listed and checked, so the user can choose which to add. A field has to be in planning before it can hold a boundary. The user adds the fields themselves on that screen; use RegisterFields instead to add all of them for the user. Some screens are restricted for some organizations and members; a refusal comes back with `opened` false and a reason to relay, not a screen.',
    argsSchema: z.object({}),
    resultSchema: z.discriminatedUnion('opened', [
      z.object({
        opened: z.literal(true),
        url: z.string().describe('The screen the client navigated to.')
      }),
      z.object({
        opened: z.literal(false),
        reason: z
          .string()
          .describe(
            'Why the screen did not open — tell the user this rather than saying they are on it.'
          )
      })
    ])
  }),
  RegisterFields: defineClientDispatchedTool({
    exposure: [ClientDispatchedToolExposure.Agent],
    description:
      'Add fields that have passes but are not yet in planning to planning, so that each can hold a boundary. Pass the fields the user agreed to add, named by client, farm, and field exactly as the field catalog spells them. To let the user pick from the list themselves, open it with OpenFieldRegistration instead. A field already in planning is skipped. Some organizations and members cannot add fields, and a field with no passes cannot be added; a refusal comes back with `registered` false and a reason to relay.',
    argsSchema: z.object({ fields: z.array(baseCffSchema).min(1) }),
    resultSchema: z.discriminatedUnion('registered', [
      z.object({
        registered: z.literal(true),
        count: z.number().int().describe('How many fields were added to planning.')
      }),
      z.object({
        registered: z.literal(false),
        reason: z
          .string()
          .describe('Why no fields were added — tell the user this rather than saying they were.')
      })
    ]),
    requiresPermission: true
  }),
  OpenBoundaryCreator: defineClientDispatchedTool({
    spokenOnCompletion: true,
    exposure: [ClientDispatchedToolExposure.Agent],
    description:
      "Open the boundary editor in Panorama web for one field and outline a boundary from the field's own as-applied passes. Name the field by client, farm, and field exactly as the field catalog spells them. The editor picks the group of passes most likely to trace the field's edge; pass `source` only to use one of the groups an earlier result listed in `otherSources`. A Generated result says which group the outline came from, which other groups exist, the outlined acres, and the field's inner areas: holes inside the outline, which are usually waterways, ponds, or ground a pass skipped. Tell the user which passes the outline came from, mention the other groups if there are any, and point out inner areas so they can check them. Nothing is saved yet: the user reviews the outline and can change it first. The editor lets the user draw more ground into the boundary or erase ground from it, drawing freehand or as a polygon, circle, or rectangle; drag a point on the outline's edge to move it, or drag anywhere else along an edge to add a point there; undo the last change; and fill every inner area at once. When the user wants to change the outline or asks how, describe what to do in those terms, for example erasing a freehand shape drawn over a strip along a road, without naming buttons or saying where they sit on screen. NotRegistered means the field has not been added to planning, and NoPasses that it has no finished passes to outline; say so rather than describing a boundary.",
    argsSchema: z.object({
      client: z.string(),
      farm: z.string(),
      field: z.string(),
      source: boundarySourceKeySchema
        .nullable()
        .describe(
          'A group from an earlier result’s `otherSources` to outline instead, given by its season, task, and one of its `dataSources`; or null to let the editor pick.'
        )
    }),
    resultSchema: z.discriminatedUnion('outcome', [
      z.object({
        outcome: z.literal(BoundaryCreatorOutcome.Generated),
        source: boundarySourceSchema,
        otherSources: z.array(boundarySourceSchema),
        outlinedAcres: z.number(),
        innerAreas: z.object({ count: z.number().int(), acres: z.number() })
      }),
      z.object({ outcome: z.literal(BoundaryCreatorOutcome.NotRegistered) }),
      z.object({ outcome: z.literal(BoundaryCreatorOutcome.NoPasses) }),
      z.object({
        outcome: z.literal(BoundaryCreatorOutcome.Failed),
        reason: z.string().describe('Why no outline was made — tell the user this.')
      })
    ]),
    timeoutMs: OPEN_BOUNDARY_CREATOR_TIMEOUT_MS
  }),
  SaveBoundary: defineClientDispatchedTool({
    exposure: [ClientDispatchedToolExposure.Agent],
    description:
      "Save the boundary outline open in the editor as the field's boundary. Name the field by client, farm, and field exactly as the field catalog spells them; nothing is saved if the editor is open on a different field. Pass `name` only when the user asked for a particular name; otherwise the name shown in the editor is used, which for an auto-created outline is already set from the field's name. HasExistingBoundary means the field already has a boundary and nothing was saved: tell the user to press Save in the editor, which asks whether to replace the existing boundary or keep both. After a save the map shows the saved boundary and the editor closes.",
    argsSchema: z.object({
      client: z.string(),
      farm: z.string(),
      field: z.string(),
      name: z
        .string()
        .nullable()
        .describe('The name the user asked for, or null to keep the name shown in the editor.')
    }),
    resultSchema: z.discriminatedUnion('outcome', [
      z.object({
        outcome: z.literal(BoundarySaveOutcome.Saved),
        name: z.string(),
        acres: z.number(),
        innerAreas: z
          .object({ count: z.number().int(), acres: z.number() })
          .describe('The inner areas the saved boundary still has.')
      }),
      z.object({ outcome: z.literal(BoundarySaveOutcome.HasExistingBoundary) }),
      z.object({ outcome: z.literal(BoundarySaveOutcome.NothingToSave) }),
      z.object({
        outcome: z.literal(BoundarySaveOutcome.Failed),
        reason: z.string().describe('Why nothing was saved — tell the user this.')
      })
    ]),
    requiresPermission: true
  }),
  FillBoundaryInnerAreas: defineClientDispatchedTool({
    spokenOnCompletion: true,
    exposure: [ClientDispatchedToolExposure.Agent],
    description:
      'Fill every inner area in the boundary outline open in the editor, so the boundary covers that ground too. Call it only after the user agrees the inner areas are not ground to leave out, such as ponds or waterways. It changes only the unsaved outline; the user still saves the boundary.',
    argsSchema: z.object({}),
    resultSchema: z.object({
      filledCount: z.number().int(),
      outlinedAcres: z.number()
    })
  }),
  ShowControls: defineClientDispatchedTool({
    exposure: [ClientDispatchedToolExposure.Agent],
    description: `Show controls from the screen the user is working on inside this chat, so they can use them without moving the chat window. Use it to hand the user only the part of a form that matters at this point in the conversation, rather than sending them to the screen behind the chat. Show a control only where using it is easier than saying it, such as picking from a long list, dragging along a histogram, or entering or checking a grid of numbers. Ask for anything the user can say in a few words, such as how many zones or what to name something, in plain language, and make the change with a tool. Pass every control that should be visible: the list replaces what is shown, and an empty list hides them all. Shown controls appear at the end of the conversation, below your reply. A required control that is not filled in yet, and a button only the user can press such as SavePrescription, take the place of the message box until the user uses them, so show one as the last thing in your turn, after saying what it is for. A required control's answer arrives as the user's next message; a button reports what happened the same way. Other controls do not interrupt: the user can use them or keep talking to you. The current value of every control the screen offers is in client context under auroraControls, so read changes there rather than asking what the user picked. Controls exist only while their screen is open; \`unavailable\` lists any you asked for that are not. Controls: ${Object.entries(
      AURORA_CONTROL_DESCRIPTIONS
    )
      .map(([control, description]) => `'${control}' (${description})`)
      .join('; ')}`,
    argsSchema: z.object({ controls: z.array(z.enum(AuroraControlId)) }),
    resultSchema: z.object({
      shown: z.array(z.enum(AuroraControlId)),
      awaitingUser: z
        .array(z.enum(AuroraControlId))
        .describe('Required controls now waiting for the user in place of the message box.'),
      unavailable: z.array(z.enum(AuroraControlId))
    })
  }),
  OpenPrescriptionZoning: defineClientDispatchedTool({
    exposure: [ClientDispatchedToolExposure.Agent],
    description:
      "Open a new prescription for one field in Panorama web, with its rate zones made automatically from a layer the user picks. Name the field as the user did, with its client and farm when you know them and null otherwise. This searches the operation's planning fields, including fields with no passes, which the field catalog does not list, so call it directly rather than looking the field up first. Ambiguous lists the fields that match; ask the user which they mean and call again with its client and farm. NotFound means no planning field has that name. The zones are drawn inside the field's default boundary. Once this returns Opened, ask which layer the zones should come from, mentioning the kinds on offer (as-applied pass layers such as yield, organic matter, or elevation; soil sample analytes; soil survey attributes), and show the ZoneSource control. When the user has chosen, call DescribeZoneTuning. NoBoundary means it has no boundary to draw zones in, so offer to outline one first. Nothing is saved until the user saves the prescription.",
    argsSchema: z.object({
      client: z.string().nullable(),
      farm: z.string().nullable(),
      field: z.string()
    }),
    resultSchema: z.discriminatedUnion('outcome', [
      z.object({
        outcome: z.literal(PrescriptionZoningOutcome.Opened),
        field: baseCffSchema,
        boundary: z.string().describe('The name of the boundary the zones are drawn in.')
      }),
      z.object({ outcome: z.literal(PrescriptionZoningOutcome.NotFound) }),
      z.object({
        outcome: z.literal(PrescriptionZoningOutcome.Ambiguous),
        fields: z.array(baseCffSchema)
      }),
      z.object({ outcome: z.literal(PrescriptionZoningOutcome.NoBoundary) }),
      z.object({
        outcome: z.literal(PrescriptionZoningOutcome.Failed),
        reason: z.string().describe('Why the prescription did not open — tell the user this.')
      })
    ])
  }),
  DescribeZoneTuning: defineClientDispatchedTool({
    exposure: [ClientDispatchedToolExposure.Agent],
    description: `Read the rate zones on the open new-prescription screen, waiting for any zoning in progress to finish. ${ZONING_RESULT_GUIDE} When explaining a first zoning, say in two or three sentences what it was made from, how many zones and why that count fits, and roughly how the field splits; do not list every number. Show ZoneBreaks with this explanation, since dragging a break is easier than describing one. Then say the zoning is a starting point: they can drag the breaks, or ask you for a different number of zones, a different way of placing breaks, a larger smallest zone, or smoother or straighter edges. Ask whether to adjust anything or go ahead.`,
    argsSchema: z.object({}),
    resultSchema: zoningSchema,
    timeoutMs: ZONING_TIMEOUT_MS
  }),
  SetZoneTuning: defineClientDispatchedTool({
    exposure: [ClientDispatchedToolExposure.Agent],
    description: `Change how the open new-prescription screen makes zones, then wait for the new zones and return them. Use it when the user asks in words, such as "try three zones" or "smoother edges"; the controls make the same changes. Pass null to leave a setting as it is. method chooses where breaks fall: naturalBreaks groups similar values together, equalCount gives each zone about the same area, equalInterval splits the value range evenly. smallestZoneAcres (0.25, 0.5, 1, or 2) and edges apply to as-applied pass layers only. ${ZONING_RESULT_GUIDE} Tell the user what changed in a sentence rather than restating the whole zoning.`,
    argsSchema: z.object({
      zoneCount: z.number().int().min(2).max(6).nullable(),
      method: z.enum(ZoningMethod).nullable(),
      smallestZoneAcres: z.number().nullable(),
      edges: z.enum(ZoneEdges).nullable()
    }),
    resultSchema: zoningSchema,
    timeoutMs: ZONING_TIMEOUT_MS
  }),
  AcceptZones: defineClientDispatchedTool({
    exposure: [ClientDispatchedToolExposure.Agent],
    description:
      'Use the zones on the open new-prescription screen for the prescription and move on to its products and rates. Call it once the user says the zones look right or wants to go ahead; do not ask them to confirm again. The zones cannot be retuned afterwards without starting over. Pass one product for each prescription the user wants from these zones; most want one. Work out what each applies from the conversation and fill every field with a sensible default rather than asking: a name from the field and its purpose, such as "ROG N80 Seeding" or "Gideon Replant Population", and a column named for each rate it sets, such as "Population" for seeding, or "Rate" when nothing suggests better. A product can set several rates at once, such as separate nitrogen and phosphorus rates from one pass, so give it a column for each rate the user mentions, and one column when they mention none. Then say in a sentence or two what you set up and that they can change any of it or add a column by telling you, and go on to the next step: show ZoneRates and ask for the rates. Only when nothing in the conversation says what the prescription applies, pass no products and ask that one question.',
    argsSchema: z.object({ products: z.array(prescriptionProductSchema) }),
    resultSchema: z.discriminatedUnion('accepted', [
      z.object({
        accepted: z.literal(true),
        zones: z.array(z.object({ name: z.string(), acres: z.number() }))
      }),
      z.object({
        accepted: z.literal(false),
        reason: z.string().describe('Why the zones were not used — tell the user this.')
      })
    ])
  }),
  FillPrescription: defineClientDispatchedTool({
    exposure: [ClientDispatchedToolExposure.Agent],
    description: `Fill in the open prescription form from what the user told you. products lists the form's products in the order the result gives them, and an entry past the last adds a product; to change only a later product, pass the earlier ones with every field null. Pass null for anything to leave it as it is. Never ask again for something the user already told you. rates sets a rate for a zone in one of that product's columns, naming the zone exactly as the result lists it; column may be null when the product has only one. When the user gives rates by description, such as "30,000 in the low zones", map them onto the zones and say which got what. Never invent a rate. Show ZoneRates as soon as the product is set, since entering a rate per zone is easier in the grid than in a message, and say the user can type rates there or tell you. After filling rates you were told, say if a rate looks implausible for the product, such as a seeding rate in single digits. Once \`missing\` is empty and the user is happy with the rates, show SavePrescription. When the user asks you to save, show SavePrescription rather than saying you saved it. Once it is saved, the map shows the prescription and the editor closes.`,
    argsSchema: z.object({
      products: z.array(
        prescriptionProductSchema.extend({
          rates: z
            .array(z.object({ zone: z.string(), column: z.string().nullable(), rate: z.number() }))
            .nullable()
        })
      )
    }),
    resultSchema: z.object({
      products: z.array(
        z.object({
          applicationType: controllableApplicationTypes,
          unit: z.string(),
          name: z.string(),
          columns: z.array(
            z.object({
              name: z.string(),
              rates: z.array(z.object({ zone: z.string(), rate: z.number().nullable() }))
            })
          )
        })
      ),
      zones: z.array(
        z.object({
          name: z.string(),
          acres: z
            .number()
            .describe(
              'Zero when the zone has no area on the map, such as one the user added in the rate grid without drawing it; saving leaves that zone out, so tell the user.'
            )
        })
      ),
      notSet: z.array(z.string()).describe('Rates you passed that were not set, each saying why.'),
      missing: z.array(z.string()).describe('What the form still needs before it can be saved.')
    })
  })
}

export type ClientDispatchedToolId = keyof typeof clientDispatchedTools

/**
 * IDs a cloud-side wrapper may dispatch to a connection other than the
 * chat's own. Derived from the per-entry exposure set, so granting remote
 * invocability is an explicit edit to the tool's definition.
 */
export type CloudRoutedToolId = {
  [
    Id in ClientDispatchedToolId
  ]: ClientDispatchedToolExposure.CloudRouted extends (typeof clientDispatchedTools)[Id]['exposure'][number]
    ? Id
    : never
}[ClientDispatchedToolId]

export type ApprovalGatedClientToolId = {
  [Id in ClientDispatchedToolId]: NonNullable<
    (typeof clientDispatchedTools)[Id]['requiresPermission']
  > extends true
    ? Id
    : never
}[ClientDispatchedToolId]

/**
 * Whether a tool carries a given exposure. The single place the set-membership
 * check lives, so its consumers — agent catalog composition, the machine
 * directory's capability flags, and the catalog audit tests — can't drift on
 * how exposure is read.
 */
export const hasExposure = (
  id: ClientDispatchedToolId,
  exposure: ClientDispatchedToolExposure
): boolean =>
  // `.some` rather than `.includes`: each entry's exposure is a narrow literal
  // array, so `includes` types its argument as that array's members and rejects
  // the full enum without a widening cast.
  clientDispatchedTools[id].exposure.some((declared) => declared === exposure)

export type ClientDispatchedToolArgs<Id extends ClientDispatchedToolId> = z.output<
  (typeof clientDispatchedTools)[Id]['argsSchema']
>

export type ClientDispatchedToolResult<Id extends ClientDispatchedToolId> = z.output<
  (typeof clientDispatchedTools)[Id]['resultSchema']
>

export const isKnownClientDispatchedToolId = (id: string): id is ClientDispatchedToolId =>
  Object.hasOwn(clientDispatchedTools, id)
